"use client";

import Image from "next/image";
import { usePathname } from "next/navigation";
import { storyblokEditable, type SbBlokData } from "@storyblok/react/rsc";

import { useTriggerAction, type BlokAction } from "@/lib/action-link";
import { regionFromPathname } from "@/lib/region";
import { resolveLink, type StoryblokLink } from "@/lib/storyblok-link";

type Asset = { filename?: string; alt?: string };

type SuccessStat = SbBlokData & {
  value?: string;
  label?: string;
  description?: string;
};

type SuccessStatDataStory = {
  content?: { stats?: SuccessStat[] };
};

type SuccessRateHeroBlok = SbBlokData & {
  title?: string;
  description?: string;
  button_label?: string;
  button_link?: StoryblokLink;
  button_action?: BlokAction | "";
  image?: Asset;
  stats_data?: string | SuccessStatDataStory;
};

export default function SuccessRateHero({ blok }: { blok: SuccessRateHeroBlok }) {
  const region = regionFromPathname(usePathname());
  const link = resolveLink(blok.button_link, region);
  const triggerAction = useTriggerAction();
  const action = blok.button_action || null;
  const showButton = Boolean(blok.button_label && (action || link.href !== "#"));
  const buttonClass =
    "inline-flex h-12 w-full items-center justify-center rounded-[8px] bg-purple-03 px-5 text-body font-bold text-white transition-colors hover:bg-purple-04 md:h-14 md:w-fit md:px-7";
  // Stats come from a shared `success_stat_data` story, expanded
  // server-side by resolve_relations.
  const resolvedStats =
    typeof blok.stats_data === "object" && blok.stats_data
      ? (blok.stats_data.content?.stats ?? [])
      : [];
  const stats = resolvedStats.filter((s) => s.value || s.label);

  return (
    <section {...storyblokEditable(blok)} className="bg-white">
      <div className="mx-auto w-full max-w-[1280px] px-[clamp(1.25rem,4vw,2.5rem)] pt-6 pb-10 md:pt-8 md:pb-[67px]">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:gap-[44px]">
          {blok.image?.filename && (
            <div className="order-first w-full lg:order-2 lg:w-[491px] lg:shrink-0">
              <div className="relative aspect-318/266 overflow-hidden rounded-[12px] md:rounded-[24px] lg:aspect-491/410">
                <Image
                  src={blok.image.filename}
                  alt={blok.image.alt || ""}
                  fill
                  sizes="(min-width: 1115px) 491px, 100vw"
                  className="object-cover"
                  priority
                />
              </div>
            </div>
          )}

          <div className="flex flex-col gap-6 lg:order-1 lg:flex-1 lg:gap-[44px]">
            <div className="flex flex-col gap-3 md:gap-8">
              {blok.title && (
                <h1 className="text-h3 text-purple-05 leading-[1.054] font-bold tracking-[0.02em] md:max-w-[640px] md:text-[64px]">
                  {blok.title}
                </h1>
              )}
              {blok.description && (
                <p className="text-body md:text-h6 leading-[1.4] text-[#595959] md:max-w-[510px] md:leading-[1.56]">
                  {blok.description}
                </p>
              )}
            </div>

            {showButton && (
              <div className="hidden md:block">
                {action ? (
                  <button
                    type="button"
                    onClick={(e) => triggerAction(action, e)}
                    className={`cursor-pointer ${buttonClass}`}
                  >
                    {blok.button_label}
                  </button>
                ) : (
                  <a
                    href={link.href}
                    {...(link.target ? { target: link.target } : {})}
                    className={buttonClass}
                  >
                    {blok.button_label}
                  </a>
                )}
              </div>
            )}
          </div>
        </div>

        {stats.length > 0 && (
          <ul className="mt-8 grid grid-cols-2 gap-4 md:mt-[103px] md:grid-cols-4 md:gap-6">
            {stats.map((stat) => (
              <li
                key={stat._uid as string}
                {...storyblokEditable(stat)}
                className="flex flex-col gap-1 rounded-[12px] border border-[#e0d1f0] bg-white px-3 py-5 shadow-[0px_3px_12px_-1px_rgba(39,6,88,0.1)] md:gap-3 md:rounded-[16px] md:px-5 md:py-[19px] md:shadow-[0px_4px_16px_-2px_rgba(39,6,88,0.1)]"
              >
                {stat.value && (
                  <p className="text-purple-03 text-[28px] leading-tight font-bold md:text-[36px]">
                    {stat.value}
                  </p>
                )}
                {stat.label && (
                  <p className="text-caption md:text-body leading-[1.56] font-bold text-[#270658]">
                    {stat.label}
                  </p>
                )}
                {stat.description && (
                  <p className="text-caption leading-[1.4] text-[#6b6685] md:text-[12px]">
                    {stat.description}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
